import React, {useEffect, useState} from 'react';
import * as api from "../../services/api";

import OfferService from "../../services/offers";
import {DateParser} from "../../services/date";

import Page from "../../layouts/Main/Page";

import {useFeedbackTools} from "../../components/FeedbackTools";
import {OptionalContent} from "../../components/OptionalContent";

import OfferForm from "./OfferForm";
import OffersList from "./OffersList";
import OfferDetailsDialog from "./OfferDetailsDialog";

import Fab from "@mui/material/Fab";
import AddIcon from "@mui/icons-material/Add";

const Offers = () => {
    const [isLoading, setIsLoading] = useState(true);
    const [displayedOffer, setDisplayedOffer] = useState(null);
    const [formState, setFormState] = useState({
        visible: false,
        initialValues: null,
        isEditMode: false
    });

    const [offers, setOffers] = useState([]);
    const [homes, setHomes] = useState([]);
    const {showErrorNotification, showSuccessNotification, askConfirmation} = useFeedbackTools();

    const [expiredOffers, activeOffers] = offers.reduce((acc, o) => {
        OfferService.utils.offerIsExpired(o) ? acc[0].push(o) : acc[1].push(o);
        return acc
    }, [[], []]);

    useEffect(() => {
        const loadData = async () => {
            try {
                const allOffersPromise = api.Offers.getAllOffers();
                const allHomesPromise = api.Offers.getAllHomes();
                const result = await Promise.all([allOffersPromise, allHomesPromise]);
                setOffers(result[0].sort(OfferService.utils.offerComparator));
                setHomes(result[1])
            } catch (e) {
                showErrorNotification(e);
            }
            setIsLoading(false);
        };

        loadData();
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const handleCloseForm = () => {
        setFormState({
            visible: false,
            initialValues: {},
            isEditMode: false,
        })
    };

    const handleShowOffer = (offer) => {
        setDisplayedOffer(offer);
    };

    const handleCloseOffer = () => {
        setDisplayedOffer(null);
    };

    const handleNewOffer = () => {
        setFormState({
            visible: true,
            initialValues: {
                titleIt: "",
                titleEn: "",
                descriptionIt: "",
                descriptionEn: "",
                startDate: null,
                endDate: null,
                price: "",
                minPeople: "",
                minNight: "",
                petsAllowed: false,
                apartmentId: "",
                conditions: []
            },
            isEditMode: false,
        })
    };

    const handleModifyOffer = (offer, isEditMode) => {
        setFormState({
            visible: true,
            initialValues: {
                ...(isEditMode && {id: offer["id"]}),
                titleIt: offer["titleIt"],
                titleEn: offer["titleEn"],
                descriptionIt: offer["descriptionIt"],
                descriptionEn: offer["descriptionEn"],
                startDate: isEditMode ? DateParser.safeParseISO(offer["startDate"], null) : null,
                endDate: isEditMode ? DateParser.safeParseISO(offer["endDate"], null) : null,
                price: offer["price"],
                minPeople: offer["minPeople"],
                minNight: offer["minNight"],
                petsAllowed: offer["petsAllowed"],
                apartmentId: offer["apartment"]["id"],
                conditions: []
            },
            isEditMode: isEditMode,
        })
    };

    const handleSave = async (offer) => {
        if (formState.isEditMode) {
            const id = offer["id"];

            // remove attributes that cannot be modify
            // (redundant operation because also the backend do this)
            delete offer["id"];
            delete offer["startDate"];
            delete offer["endDate"];
            delete offer["apartmentId"];

            try {
                const updatedOffer = await api.Offers.updateOffer(id, offer);
                const newOffers = offers.map(o => o["id"] === updatedOffer["id"] ? updatedOffer : o);
                setOffers(newOffers);
                handleCloseForm();
                showSuccessNotification("L'offerta è stata modificata");
            } catch (e) {
                showErrorNotification(e);
            }
        } else {
            // create new offer
            try {
                const createdOffer = await api.Offers.createOffer(offer);
                const newOffers = offers.concat([createdOffer]).sort(OfferService.utils.offerComparator);
                setOffers(newOffers);
                handleCloseForm();
                showSuccessNotification("Hai creato una nuova offerta");
            } catch (e) {
                showErrorNotification(e);
            }
        }

    };

    const handleDelete = (id) => {
        const innerDelete = async () => {
            try {
                await api.Offers.deleteOffer(id);
                const newOffers = offers.filter(o => o.id !== id);
                setOffers(newOffers);
            } catch (e) {
                showErrorNotification(e);
            }
        };

        askConfirmation({
            title: "Elimina Offerta",
            description: `Confermi l'eliminazione dell'offerta selezionata?`,
            confirmationText: 'Si',
            cancellationText: 'No',
            onConfirm: innerDelete
        });
    };

    return (
        <Page title="Offerte" loading={isLoading}>
            <OptionalContent hasContent={offers.length > 0}
                             noContentMessage="Non sono presenti offerte"
                             showIcon={true}>

                <OffersList offers={activeOffers}
                            onShow={handleShowOffer}
                            onModify={handleModifyOffer}
                            onDelete={handleDelete}/>

                {
                    expiredOffers.length > 0 &&
                    <OffersList offers={expiredOffers}
                                title="Offerte scadute"
                                onShow={handleShowOffer}
                                onModify={handleModifyOffer}
                                onDelete={handleDelete}/>
                }
            </OptionalContent>

            <Fab variant="extended"
                 color="primary"
                 size='medium'
                 sx={{position: "fixed", bottom: 0, right: 0, mr: {xs:2, md:4}, mb: {xs:2, md:4}}}
                 onClick={handleNewOffer}>
                <AddIcon sx={{mr: 1}}/>
                Nuova offerta
            </Fab>

            {
                displayedOffer &&
                <OfferDetailsDialog offer={displayedOffer}
                                    homes={homes}
                                    onClose={handleCloseOffer}/>
            }

            {
                formState.visible &&
                <OfferForm homes={homes}
                           initialValues={formState.initialValues}
                           isEditMode={formState.isEditMode}
                           onClose={handleCloseForm}
                           onSave={handleSave}/>
            }
        </Page>
    )
};

export default Offers;